import { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { WEIGHT_UNIT } from '../../../../types/enum';
import { FORMAT, formatDate, getLastDate } from '../../../../lib/date';
import { convertWeight } from '../../../../lib/utils';
import { ReactComponent as CalendarSearchSvg } from '../../../../assets/svg/calendar-search.svg';
import CreateDialog from './createDialog';
import LogBook from './logBook';
import BMIBar from '../../../../components/bmiBar';
import request from '../../../../lib/request';
import usePreference from '../../../../hook/usePreference';
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const WeightProgress = () => {
  const { preference, updateUserPreference } = usePreference();

  const createDialogRef = useRef(null);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [dataRange, setDataRange] = useState('30');
  const [logs, setLogs] = useState([]);
  const [tableData, setTableData] = useState({
    headers: ['Date', 'Weight(kg)', 'Change', 'BMI'],
    bodies: [],
  });

  useEffect(() => {
    const today = formatDate(new Date());
    const lastDate = formatDate(getLastDate(dataRange));

    getWeightLogs(lastDate, today);
  }, [dataRange, preference]);

  const getWeightLogs = async (startDate, endDate) => {
    try {
      const { result } = await request.get(
        `/log/weight?startDate=${startDate}&endDate=${endDate}`
      );
      if (result) {
        const refinedLogs = result.map((log) => ({
          ...log,
          weight:
            preference.weightUnit === WEIGHT_UNIT.LB
              ? convertWeight(log.weight, WEIGHT_UNIT.KG, WEIGHT_UNIT.LB)
              : log.weight,
          change:
            preference.weightUnit === WEIGHT_UNIT.LB
              ? convertWeight(log.change, WEIGHT_UNIT.KG, WEIGHT_UNIT.LB)
              : +log.change,
        }));

        setLogs(refinedLogs);

        const bodies = refinedLogs.map((log) => [
          log.logDate,
          log.weight,
          +log.change === 0 ? '-' : log.change,
          log.bmi,
        ]);
        setTableData((prev) => ({
          headers: prev.headers.map((header) =>
            header === 'Weight(kg)'
              ? `Weight(${
                  preference.weightUnit === WEIGHT_UNIT.LB ? 'lb' : 'kg'
                })`
              : header
          ),
          bodies,
        }));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const refresh = async () => {
    const today = formatDate(new Date());
    const lastDate = formatDate(getLastDate(dataRange));

    await getWeightLogs(lastDate, today);
  };

  const openCreateDialog = () => {
    if (createDialogRef.current) {
      createDialogRef.current.showModal();
      setIsCreateDialogOpen(true);
    }
  };

  const closeCreateDialog = () => {
    if (createDialogRef.current) {
      createDialogRef.current.close();
      setIsCreateDialogOpen(false);
    }
  };

  const handleSelectRange = (e) => {
    setDataRange(e.target.value);
  };

  return (
    <div className='space-y-4'>
      <div className='flex justify-between items-center'>
        <div className='flex items-center gap-2'>
          <button
            className={`relative inline-flex items-center h-6 rounded-full w-11 ${
              preference.weightUnit === WEIGHT_UNIT.KG
                ? 'bg-yellow-500'
                : 'bg-gray-200'
            }`}
            onClick={() =>
              updateUserPreference({
                weightUnit:
                  preference.weightUnit === WEIGHT_UNIT.KG
                    ? WEIGHT_UNIT.LB
                    : WEIGHT_UNIT.KG,
              })
            }
          >
            <span className='sr-only'>Toggle weight unit</span>
            <span
              className={`${
                preference.weightUnit === WEIGHT_UNIT.KG
                  ? 'translate-x-6'
                  : 'translate-x-1'
              } inline-block w-4 h-4 transform bg-white rounded-full transition-transform duration-200 ease-in-out`}
            />
          </button>
          <span className='text-white text-sm font-medium'>
            {preference.weightUnit?.toLowerCase()}
          </span>
        </div>
        <select
          onChange={handleSelectRange}
          className='rounded-full px-2 py-1 bg-tertiary  text-white'
        >
          <option value='30'>Last 30 days</option>
          <option value='90'>Last 90 days</option>
          <option value='half'>Last 6 months</option>
          <option value='year'>Last 12 months</option>
        </select>
        <CreateDialog
          dialogRef={createDialogRef}
          closeDialog={closeCreateDialog}
          isDialogOpen={isCreateDialogOpen}
          refreshData={refresh}
          preference={preference}
        />
      </div>
      {logs.length > 0 && (
        <div className='rounded-xl px-3 py-2 bg-secondary'>
          <Line
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: false,
                },
                title: {
                  display: true,
                  text: `Weight (${
                    preference.weightUnit === WEIGHT_UNIT.LB ? 'lb' : 'kg'
                  })`,
                },
              },
            }}
            data={{
              labels: logs.map((log) =>
                formatDate(new Date(log.logDate), FORMAT.MONTHDATE)
              ),
              datasets: [
                {
                  label: preference.weightUnit === WEIGHT_UNIT.LB ? 'lb' : 'kg',
                  data: logs.map((log) => log.weight),
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                },
              ],
            }}
          />
        </div>
      )}
      {logs.length > 0 && <BMIBar />}
      {tableData.bodies.length > 0 && (
        <LogBook
          tableData={tableData}
          openCreateDialog={openCreateDialog}
          logs={logs}
          refresh={refresh}
          preference={preference}
        />
      )}
      {logs.length === 0 && (
        <div className='flex flex-col justify-center items-center'>
          <div className='flex flex-col justify-center items-center py-12'>
            <div className='w-20 h-20 opacity-10'>
              <CalendarSearchSvg
                className='w-full h-full'
                fill='white'
              />
            </div>
            <p className='opacity-20 text-center text-white'>No logs</p>
          </div>
          <button
            className='font-bold border rounded-full flex justify-center items-center size-12 text-white'
            onClick={openCreateDialog}
          >
            +
          </button>
        </div>
      )}
    </div>
  );
};

export default WeightProgress;
